import React from 'react';
import { useBrand } from '../../../app/config/brand';
import { Card, PageTitle, SectionTitle, InfoBanner } from '../../../shared/components/ui';
import { useMcpConfig } from '../hooks/useMcpConfig';

/**
 * /settings/integrations — the config-gated MCP discoverability surface.
 *
 * Renders nothing while the MCP server is dark: `useMcpConfig().enabled` is
 * false while loading, on a fetch error, or while the endpoint reports
 * `enabled: false` (R-CONFIG-GATE). The route stays protected/reachable — only
 * the page BODY is empty when disabled; MainLayout chrome still wraps it.
 *
 * When it renders it shows client-aware copy (a Claude Code self-serve
 * one-liner and the claude.ai org-admin connector path) plus the boundary
 * disclaimer stating that final court submission and payments stay in the
 * portal (R-DISCLAIMER).
 */
const IntegrationsPage: React.FC = () => {
  const brand = useBrand();
  const { config, enabled } = useMcpConfig();

  if (!enabled) return null;

  const url = config?.url ?? '';

  return (
    <div data-testid="mcp-integrations-surface" className="space-y-4">
      <PageTitle subtitle="Connect a Claude client to prepare and review your filings.">
        Integrations
      </PageTitle>

      <div className="mb-4">
        <InfoBanner>
          Final court submission and payments stay in the portal — connected Claude clients can
          prepare and review filings, but you complete submission and pay here.
        </InfoBanner>
      </div>

      <Card>
        <SectionTitle>Claude Code (self-serve)</SectionTitle>
        <p className="text-sm mb-3" style={{ color: brand.colors.textLight }}>
          Add the EFSP tools to your Claude Code session with a single command:
        </p>
        <pre
          className="text-xs rounded-lg p-3 overflow-x-auto"
          style={{ backgroundColor: brand.colors.bgLight, color: brand.colors.text }}
        >
          <code>{`claude mcp add --transport http lds-efsp ${url}`}</code>
        </pre>
      </Card>

      <Card>
        <SectionTitle>claude.ai (org admin)</SectionTitle>
        <p className="text-sm mb-2" style={{ color: brand.colors.textLight }}>
          A workspace admin can add EFSP as a custom connector for your organization:
        </p>
        <ol className="text-sm list-decimal pl-5 space-y-1" style={{ color: brand.colors.text }}>
          <li>Open claude.ai → Settings → Connectors.</li>
          <li>Choose “Add custom connector”.</li>
          <li>
            Paste the server URL:{' '}
            <span className="font-mono" style={{ color: brand.colors.link }}>{url}</span>
          </li>
        </ol>
      </Card>
    </div>
  );
};

export default IntegrationsPage;
